import {
  useFonts,
  useImages,
  useLoader,
  useNavigateQuery,
  useNetworkListener
} from '@CS/hooks';

import Time from '@/utils/Time';
import { Theme } from '@/theme';
import API from '@/services/api';
import { connect } from 'react-redux';
import { useRecaptcha } from '@/hooks';
import FONTS from '@/assets/fonts.json';
import { isDEV } from '@/utils/Location';
import { Images } from '@/assets/images';
import { Login, Landing } from '@/routes';
import { User, MCD } from '@/services/app';
// import { sentryDSN } from '@/utils/API';
import { useState, useEffect } from 'react';

import CloseButton from '@/components/CloseButton';
import { getLogged } from '@/redux/auth/selectors';
import type { UserState } from '@/redux/user/types';
// import { RequestError } from '@CS/components/Popups';
import { getUserState } from '@/redux/user/selectors';
// import type { RequestErrorInfo } from '@/services/api/types';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AppWrapper, ContentWrapper } from '@CS/app/App.styles';
// import { withSentryReactRouterV6Routing } from '@sentry/react';
import TranslationProvider from '@CS/components/TranslationProvider';

type ApplicationProps = {
  user: UserState;
  logged?: boolean;
};

const Application = ({ user, logged }: ApplicationProps) => {
  const { search } = useLocation();
  const fontsLoaded = useFonts(FONTS);
  const navigate = useNavigateQuery();

  const network = useNetworkListener();
  const { recaptcha } = useRecaptcha();
  const imageLoaded = useImages(Images);

  const [loading, setLoading] = useState(true);
  const { email, points } = user ?? {};
  // const cancelLogin = () => setLoginError(void 0);

  const loader = useLoader(
    !fontsLoaded || !recaptcha || !imageLoaded || loading,
    { color: Theme.color.red }
  );

  // const [loginError, setLoginError] = useState<RequestErrorInfo>();

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' && toggleFullscreen();

  const getUserData = () =>
    Promise.all([User.checkUserPoints(), User.checkUserData()]);

  const toggleFullscreen = () => {
    if (isDEV()) return;
    MCD.toggleFullscreen();
    MCD.orientationScreen();
  };

  const initApplication = () => {
    User.checkUserLanguage();
    User.checkUserCountry();
    toggleFullscreen();
    API.revokeToken();
    handleAuth();
  };

  const loginRedirect = () => {
    setLoading(false);
    navigate('/login');
  };

  const onLogin = async () => {
    toggleFullscreen();
    await getUserData();
    setLoading(false);
    navigate('/');
  };

  const homeRedirect = () => {
    setLoading(false);
    !isDEV() && navigate('/');
  };

  const handleAuth = () =>
    (!email || typeof points !== 'number') &&
    getUserData().then(homeRedirect).catch(loginRedirect);

  useEffect(() => {
    API.setCampaignData(user);
  }, []);

  useEffect(() => {
    MCD.App.mcd && !loading && initApplication();
  }, [MCD.App.mcd]);

  useEffect(() => {
    Time.setCurrentTime(search);
  }, [search]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mcdBridgeReady', initApplication);
    (MCD.App.mcd || isDEV()) && initApplication();

    return () => {
      document.removeEventListener('mcdBridgeReady', initApplication);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  /**
   * Sentry Tracing Routes Wrapper:
   *
  const RoutesWrapper = sentryDSN
    ? withSentryReactRouterV6Routing(Routes)
    : Routes; */

  return (
    loader ||
    network || (
      <AppWrapper>
        <CloseButton onClick={MCD.openHome} />

        <TranslationProvider lang={user.language}>
          <ContentWrapper>
            <Routes>
              {/* <RoutesWrapper> */}
              <Route path="/login" element={<Login onLogin={onLogin} />} />
              <Route path="/" element={<Landing onLogin={onLogin} />} />
              {/* </RoutesWrapper> */}
            </Routes>

            {/* loginError && (
              <RequestError info={loginError} onClose={cancelLogin} />
            ) */}
          </ContentWrapper>
        </TranslationProvider>
      </AppWrapper>
    )
  );
};

const mapStateToProps = (state: never) => ({
  user: getUserState(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(Application);
